import { Serializable } from '@/models/core/serializable';
import { GEnum } from '@/constants/enum.defination';
import { GradeSchoolClass, SchoolClass } from '@/models/school-class/school-class';
import { Student } from '@/models/student/student';
import { Activity } from '@/models/activity/activity';
import { Survey } from '@/models/jgrow/survey';
import { SurveyActivityAnswer, SurveyCompetencyAnswer, SurveyCompetencySelectActivity } from '@/models/jgrow/survey-answer';
import { StatsCompetencyBoxplot } from '@/models/statistics/stats-competency-boxplot';

export class Event implements Serializable<Event> {
  ID: number;
  eventName: string = ''; // 受検コース名
  companyID: number = 0; // 学校ID
  destination: number = 1; // 行き先
  startDate: Date = new Date(); // イベント開始日
  endDate: Date = new Date(); // イベント終了日
  activities: number[] = [];
  competencies: number[] = [];
  q1: string = '';
  q1Enable: boolean = false;
  q2: string = '';
  q2Enable: boolean = false;
  q3: string = '';
  q3Enable: boolean = false;
  surveys: Survey[] = [];
  giftCourses: EventGiftCcourse[] = [];
  schoolClassIDs: number[] = [];
  userIDs: number[] = [];
  students: Student[] = [];
  noAssignedExamineeCount: number = 0;

  // このイベント情報が編集可能か
  isEditable(): boolean {
    const now = new Date();
    const survey1 = this.surveys.find((suv: Survey) => suv.type === 1);
    return now < survey1.startDate;
  }

  deserialize(input: any): Event {
    this.ID = input.ID;
    this.eventName = input.eventName;
    this.companyID = input.companyID;
    this.destination = input.destination;
    this.startDate = new Date(input.startDate);
    this.endDate = new Date(input.endDate);
    this.q1 = input.Q1;
    this.q1Enable = input.q1Enable;
    this.q2 = input.Q2;
    this.q2Enable = input.q2Enable;
    this.q3 = input.Q3;
    this.q3Enable = input.q3Enable;
    this.competencies = input.competencies;
    this.activities = input.activities;
    if (input.hasOwnProperty('surveys') && input.surveys !== undefined && input.surveys !== null && input.surveys.length) {
      this.surveys = input.surveys.map((svy: Survey) => new Survey().deserialize(svy));
    }
    if (input.hasOwnProperty('giftCourses') && input.giftCourses !== undefined && input.giftCourses !== null && input.giftCourses.length) {
      this.giftCourses = input.giftCourses.map((egc: EventGiftCcourse) => new EventGiftCcourse().deserialize(egc));
    }
    if (input.hasOwnProperty('schoolClassIDs') && input.schoolClassIDs !== undefined && input.schoolClassIDs !== null && input.schoolClassIDs.length) {
      this.schoolClassIDs = input.schoolClassIDs;
    }
    if (input.hasOwnProperty('userIDs') && input.userIDs !== undefined && input.userIDs !== null && input.userIDs.length) {
      this.userIDs = input.userIDs;
    }
    this.noAssignedExamineeCount = input.noAssignedExamineeCount;
    return this;
  }
}

export class EventGiftCcourse implements Serializable<EventGiftCcourse> {
  ID: number = 0;
  type: number = 0; // 1:前受検, 2:後受検
  title: string = '';
  startTime: Date;
  deadline: Date;

  deserialize(input: any): EventGiftCcourse {
    this.ID = input.giftCourseID;
    this.type = input.type;
    this.title = input.title;
    this.startTime = new Date(input.startTime);
    this.deadline = new Date(input.deadline);

    return this;
  }
}

/**
 * あるイベントにおける生徒たちの回答まとめ
 * SurveyAnalyzeModule.answers の内容を生徒単位にしている
 */
export class EventSurveyStudentAnswer {
  userID: number;
  // 事前アンケートにおけるアクティビティへの期待度の回答
  beforeActivityAnswers: SurveyActivityAnswer[] = [];
  // 事後アンケートにおけるアクティビティへの参加度の回答
  afterActivityAnswers: SurveyActivityAnswer[] = [];
  // 事後アンケートにおけるコンピテンシーに対する回答
  afterCompetencyAnswers: SurveyCompetencyAnswer[] = [];
  // 事後アンケートにおけるコンピテンシーとアクティビティの関連
  afterSelectActivities: SurveyCompetencySelectActivity[] = [];

  // 事前受検のコンピテンシースコア
  beforeExamCompetencyScores: StatsCompetencyBoxplot;
  // 事後受検のコンピテンシースコア
  afterExamCompetencyScores: StatsCompetencyBoxplot;

  constructor(id: number) {
    this.userID = id;
  }

  isCompetencyGrowth(id: number): boolean {
    let beforeScore = 0,
      afterScore = 0;
    if (this.beforeExamCompetencyScores && this.beforeExamCompetencyScores.competencyScores.has(id)) {
      beforeScore = this.beforeExamCompetencyScores.competencyScores.get(id);
    }
    if (this.afterExamCompetencyScores && this.afterExamCompetencyScores.competencyScores.has(id)) {
      afterScore = this.afterExamCompetencyScores.competencyScores.get(id);
    }
    if (beforeScore && afterScore) {
      return beforeScore < afterScore;
    }
    return false;
  }

  // あるコンピテンシーについてポジティブ回答したか（ポジティブ = 58以上）
  isCompetencyPositive(id: number): boolean {
    const found = this.afterCompetencyAnswers.find((compeAnswer: SurveyCompetencyAnswer) => {
      if (compeAnswer.competencyID === id) return compeAnswer.score > 57;
    });
    return found ? true : false;
  }
}

/**
 * イベント分析画面「コンピテンシータブ」の表に使うデータ保持用クラス
 */
export class EventSurveyCompetencyGrowthTotal {
  // 横方向のコンピテンシーID（このコンピテンシーが成長しているか）
  rowCompetencyID: number;
  // 縦方向のコンピテンシーID（このコンピテンシーにポジティブ回答したか）
  colCompetencyID: number;
  // このコンピテンシーが成長した生徒の数
  growthCount: number = 0;
  // このコンピテンシーについてポジティブ回答した数
  positiveCount: number = 0;

  constructor(rowID: number, colID: number) {
    this.rowCompetencyID = rowID;
    this.colCompetencyID = colID;
  }

  get rate(): number {
    if (this.growthCount > 0 && this.positiveCount > 0) {
      return Math.floor((this.positiveCount / this.growthCount) * 1000) / 10;
    }
    return 0;
  }
}

export class UpdateEventsStudentsResult implements Serializable<UpdateEventsStudentsResult> {
  updatedCount: number;

  deserialize(input: any): UpdateEventsStudentsResult {
    this.updatedCount = input.updatedCount;
    return this;
  }
}
